import type { SocialLinkItem } from "@/components/molecules/SocialLinks/SocialLinks";
import type { SpaceContact } from "@/types";
import { generateWhatsAppLink } from "./linksGenerator";

const SPACE_CONTACT_KEYS = [
  "email",
  "whatsapp",
  "phoneForCalls",
  "instagram",
  "facebook",
  "tiktok",
  "x",
  "youtube",
  "externalLink",
  "maps",
] as const satisfies readonly (keyof SpaceContact)[];

type SpaceContactKey = (typeof SPACE_CONTACT_KEYS)[number];

const isNonEmpty = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Strips Prisma metadata and keeps only SpaceContact fields
 */
export const pickSpaceContact = (raw: unknown): SpaceContact => {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  const source = raw as Record<string, unknown>;
  const contact: SpaceContact = {};

  for (const key of SPACE_CONTACT_KEYS) {
    const value = source[key];
    if (typeof value === "string" && isNonEmpty(value)) {
      contact[key] = value.trim();
    }
  }

  return contact;
};

/**
 * Normalizes a social handle from username or profile URL
 */
export const normalizeSocialHandle = (value: string): string => {
  const trimmed = value.trim().replace(/^@+/, "");

  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  try {
    const pathname = new URL(trimmed).pathname.replace(/\/+$/, "");
    const segment = pathname.split("/").filter(Boolean).pop();
    return segment?.replace(/^@+/, "") ?? trimmed;
  } catch {
    return trimmed;
  }
};

const buildHref = (
  key: SpaceContactKey,
  value: string,
  spaceName: string,
): Pick<SocialLinkItem, "href" | "copyOnDesktop" | "copyValue"> => {
  switch (key) {
    case "email":
      return {
        href: `mailto:${value}`,
        copyOnDesktop: true,
        copyValue: value,
      };
    case "whatsapp":
      return {
        href: generateWhatsAppLink(value, `🎭 Hola ${spaceName}!`),
      };
    case "phoneForCalls":
      return {
        href: `tel:${value}`,
        copyOnDesktop: true,
        copyValue: value,
      };
    case "instagram":
      return {
        href: `https://instagram.com/${normalizeSocialHandle(value)}`,
      };
    case "facebook":
      return {
        href: `https://facebook.com/${normalizeSocialHandle(value)}`,
      };
    case "tiktok":
      return {
        href: `https://www.tiktok.com/@${normalizeSocialHandle(value)}`,
      };
    case "x":
      return {
        href: `https://x.com/${normalizeSocialHandle(value)}`,
      };
    case "youtube":
      return {
        href: `https://www.youtube.com/@${normalizeSocialHandle(value)}`,
      };
    case "externalLink":
    case "maps":
      return { href: value };
    default:
      return { href: value };
  }
};

const buildAriaLabel = (
  key: SpaceContactKey,
  spaceName: string,
): string => {
  const labels: Record<SpaceContactKey, string> = {
    email: `Enviar email a ${spaceName}`,
    whatsapp: `Ir a WhatsApp de ${spaceName}`,
    phoneForCalls: `Llamar a ${spaceName}`,
    instagram: `Ir al Instagram de ${spaceName}`,
    facebook: `Ir al Facebook de ${spaceName}`,
    tiktok: `Ir al TikTok de ${spaceName}`,
    x: `Ir a X de ${spaceName}`,
    youtube: `Ir al YouTube de ${spaceName}`,
    externalLink: `Ir al sitio web de ${spaceName}`,
    maps: `Ver ubicación de ${spaceName} en el mapa`,
  };

  return labels[key];
};

/**
 * Builds SocialLinkItem[] from a space contact sheet
 */
export const buildSocialLinksFromContact = (
  contact: SpaceContact,
  spaceName: string,
): SocialLinkItem[] => {
  const links: SocialLinkItem[] = [];

  for (const key of SPACE_CONTACT_KEYS) {
    const value = contact[key];
    if (!isNonEmpty(value)) {
      continue;
    }

    links.push({
      icon: key,
      ariaLabel: buildAriaLabel(key, spaceName),
      ...buildHref(key, value, spaceName),
    });
  }

  return links;
};
